import { useMemo } from "react";
import { DateTime } from "luxon";
import { Colors } from "swing-components";

import {
  AlertError,
  FeedbackExperiences,
  FeedbackReasons,
  FeedbackSelections,
  ModalSubProfileEdit,
  ModalSubProfileEditBody,
  ModalSubProfileEditHeader,
} from "~components";
import { RequestData, translate } from "~utils";

type ModalSchoolFeedbackSubmittedProps = {
  feedback?: RequestData["schoolFeedback"];
  onDismiss: () => void;
};

export function ModalSchoolFeedbackSubmitted(props: ModalSchoolFeedbackSubmittedProps) {
  const { feedback, onDismiss } = props;

  // Create a map of the order of reasons based on FeedbackSelections
  // Note: feedbackOrderMap is defined here rather than outside the
  // component to avoid potential access issues while FeedbackSelections loads.
  const feedbackOrderMap = useMemo(
    () =>
      Object.keys(FeedbackSelections).reduce(
        (map, key, idx) => {
          map[key] = idx;
          return map;
        },
        {} as Record<string, number>,
      ),
    [],
  );

  if (!feedback) {
    return <AlertError />;
  }

  // Sort the feedback reasons based on the order defined in FeedbackSelections
  const orderedReasons = feedback.reasons
    ?.slice()
    .sort((a, b) => feedbackOrderMap[a] - feedbackOrderMap[b]);

  const reasonsTitle =
    feedback?.experience === "POSITIVE"
      ? translate("SCHOOL_FEEDBACK_SUBMITTED_REASONS_LABEL_POSITIVE")
      : translate("SCHOOL_FEEDBACK_SUBMITTED_REASONS_LABEL_DEFAULT");

  return (
    <ModalSubProfileEdit>
      <ModalSubProfileEditHeader onDismiss={onDismiss} title="Review" />
      <ModalSubProfileEditBody>
        <div>
          {translate("SCHOOL_FEEDBACK_SUBMITTED_ON_MSG", {
            date: formatSubmittedDate(feedback.date),
          })}
        </div>
        <div
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: Colors.slate200,
            margin: "8px 0",
          }}
        ></div>
        <div style={{ display: "grid" }}>
          <span style={{ fontWeight: "var(--swing-font-semibold" }}>
            {translate("SCHOOL_FEEDBACK_SUBMITTED_OVERALL_LABEL")}:
          </span>
          {feedback.experience && <span>{FeedbackExperiences[feedback.experience]}</span>}
        </div>
        <div style={{ margin: "16px 0" }}>
          <span style={{ fontWeight: "var(--swing-font-semibold" }}>{reasonsTitle}?</span>
          <ul style={{ margin: "0", paddingLeft: "24px" }}>
            {orderedReasons?.map((reason: FeedbackReasons) => {
              if (reason === "OTHER") {
                return (
                  <li key={reason}>
                    {FeedbackSelections[reason]}: {feedback.otherReason}
                  </li>
                );
              }
              return <li key={reason}>{FeedbackSelections[reason]}</li>;
            })}
          </ul>
        </div>
        <div style={{ fontWeight: "var(--swing-font-semibold" }}>
          {translate("SCHOOL_FEEDBACK_SUBMITTED_ADDITIONAL_INFO_TITLE")}:
        </div>
        {feedback.comments ? (
          <span>{feedback.comments}</span>
        ) : (
          <span style={{ fontStyle: "italic" }}>{translate("LABEL_NONE")}</span>
        )}
      </ModalSubProfileEditBody>
    </ModalSubProfileEdit>
  );
}

function formatSubmittedDate(date: string) {
  if (date.length != 0) {
    return `${DateTime.fromISO(date).monthShort} ${DateTime.fromISO(date).day}, ${DateTime.fromISO(date).year}`;
  } else return translate("SCHOOL_FEEDBACK_SUBMITTED_DEFAULT_DATE_MSG");
}
